import type { AccessibilitySettings, ColorBlindFilter } from '@/lib/a11y/types'

export interface SettingsSummaryItem {
  /** Stabilan ključ (za React key i testove). */
  key: string
  /** Čitljiv opis na srpskom za ekran pregleda. */
  label: string
}

const COLOR_BLIND_LABELS: Record<Exclude<ColorBlindFilter, 'none'>, string> = {
  protanopia: 'protanopija',
  deuteranopia: 'deuteranopija',
  tritanopia: 'tritanopija',
}

const FONT_SIZE_LABELS = {
  lg: 'Veći tekst',
  xl: 'Velik tekst',
  xxl: 'Najveći tekst',
} as const

/**
 * Pretvara (parcijalna) izvedena podešavanja u listu čitljivih stavki.
 * Prikazuje samo ono što odstupa od podrazumevanog (uključeni boolean-i,
 * ne-default enum-i) — tako korisnik vidi tačno šta smo promenili za njega.
 */
export function describeDerivedSettings(
  settings: Partial<AccessibilitySettings>,
): SettingsSummaryItem[] {
  const items: SettingsSummaryItem[] = []

  if (settings.fontSize && settings.fontSize in FONT_SIZE_LABELS) {
    items.push({
      key: 'fontSize',
      label: FONT_SIZE_LABELS[settings.fontSize as keyof typeof FONT_SIZE_LABELS],
    })
  }
  if (settings.fontFamily === 'dyslexic') {
    items.push({ key: 'fontFamily', label: 'Font prilagođen disleksiji (OpenDyslexic)' })
  }
  if (settings.letterSpacing === 'wide') {
    items.push({ key: 'letterSpacing', label: 'Veći razmak između slova' })
  }
  if (settings.wordSpacing === 'wide') {
    items.push({ key: 'wordSpacing', label: 'Veći razmak između reči' })
  }
  if (settings.lineHeight === 'wide') {
    items.push({ key: 'lineHeight', label: 'Veći razmak između linija' })
  }
  if (settings.contrastMode === 'high') {
    items.push({ key: 'contrastMode', label: 'Visoki kontrast' })
  }
  if (settings.colorBlindFilter && settings.colorBlindFilter !== 'none') {
    items.push({
      key: 'colorBlindFilter',
      label: `Filter boja za daltonizam (${COLOR_BLIND_LABELS[settings.colorBlindFilter]})`,
    })
  }
  if (settings.reducedMotion) {
    items.push({ key: 'reducedMotion', label: 'Smanjene animacije' })
  }
  if (settings.enhancedFocus) {
    items.push({ key: 'enhancedFocus', label: 'Naglašen okvir fokusa' })
  }
  if (settings.easyReadDefault) {
    items.push({ key: 'easyReadDefault', label: 'Lako čitanje uključeno podrazumevano' })
  }
  if (settings.captionsDefault) {
    items.push({ key: 'captionsDefault', label: 'Titlovi uvek uključeni' })
  }
  if (settings.signLanguagePreferred) {
    items.push({ key: 'signLanguagePreferred', label: 'Prikaz znakovnog jezika kad postoji' })
  }
  if (settings.distractionFree) {
    items.push({ key: 'distractionFree', label: 'Režim bez ometanja' })
  }
  if (settings.ttsKaraoke) {
    items.push({ key: 'ttsKaraoke', label: 'Isticanje teksta tokom čitanja naglas' })
  }

  return items
}
